import React, { useState, useEffect } from 'react';
import { Crown, Check, Sparkles, Zap, X, Loader2, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../utils';
import { shouldUseStoreKit, purchaseProduct, verifyWithBackend, getLocalizedPrice } from '../../utils/iosPurchases';

const API_URL = getApiUrl();

const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  CHF: 'CHF ',
  SEK: 'kr',
  DKK: 'kr',
  NOK: 'kr',
  PLN: 'zł',
  CAD: 'C$',
  AUD: 'A$'
};

// New 3-tier pricing structure
const TIER_FEATURES = {
  free: [
    'Up to 200 wardrobe items',
    '10 wishlist items/month',
    '1 bulk upload/month (up to 15 items)',
    'Unlimited AI Detect & saved models',
    'Unlimited calendar outfits & canvas saves',
    '1 standard try-on/month',
    '15 studio BG removals/month',
    '10 background changes/month',
    '1 image edit/month'
  ],
  basic: [
    'Everything in Free, plus:',
    'Up to 600 wardrobe items',
    '25 wishlist items/month',
    '3 bulk uploads/month (15 items each)',
    '30 AI chats/month',
    '15 standard try-ons/month',
    '2 fashion videos/month',
    '1 360° view/month',
    '3 creative visions/month',
    '30 background changes/month',
    '35 studio BG removals/month',
    '15 image edits/month'
  ],
  premium: [
    'Everything in Basic, plus:',
    'Unlimited wardrobe items',
    'Unlimited AI chats & wishlist',
    '40 standard try-ons/month',
    '4 fashion videos/month',
    '2 360° views/month',
    '10 creative visions/month',
    '50 background changes/month',
    '60 studio BG removals/month',
    '6 bulk uploads/month (15 items each)',
    '25 image edits/month'
  ]
};

export default function PricingModal({ isOpen, onClose, region }) {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('premium-monthly');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    
    const fetchPricing = async () => {
      try {
        const currency = region?.currency || 'USD';
        const res = await fetch(`${API_URL}/subscription/pricing?currency=${currency}`);
        const data = await res.json();
        setPricing(data);
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPricing();
  }, [isOpen, region]);

  const handleSubscribe = async () => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      onClose();
      navigate('/login');
      return;
    }

    if (selectedPlan === 'free') {
      onClose();
      return;
    }

    setCheckoutLoading(true);
    setError('');

    // iOS native: use StoreKit instead of Stripe
    if (shouldUseStoreKit()) {
      try {
        await purchaseProduct(selectedPlan);
        // Purchase approved and verified with backend
        onClose();
        window.location.reload();
      } catch (err) {
        if (err.message === 'cancelled') {
          // User cancelled — no error needed
        } else {
          setError(err.message || 'Purchase failed. Please try again.');
        }
      } finally {
        setCheckoutLoading(false);
      }
      return;
    }

    // Web: use Stripe Checkout
    try {
      const res = await fetch(`${API_URL}/stripe/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: selectedPlan,
          currency: region?.currency || 'USD'
        })
      });

      const data = await res.json();

      if (data.url) {
        window.location.href = data.url;
      } else if (data.error) {
        const errVal = data.details || data.error;
        setError(typeof errVal === 'object' ? (errVal.message || 'Checkout failed') : errVal);
      } else {
        setError('Unknown error occurred');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!isOpen) return null;

  const currency = region?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';
  
  // New pricing
  const basicPrice = pricing?.plans?.find(p => p.id === 'basic-monthly')?.price || 7.99;
  const premiumPrice = pricing?.plans?.find(p => p.id === 'premium-monthly')?.price || 14.99;

  const isIOS = shouldUseStoreKit();

  // On iOS, use App Store localized prices if available
  const displayBasicPrice = (isIOS && getLocalizedPrice('basic-monthly')) || `${symbol}${basicPrice}`;
  const displayPremiumPrice = (isIOS && getLocalizedPrice('premium-monthly')) || `${symbol}${premiumPrice}`;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-800">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r from-stone-600 to-terracotta-600 mb-4">
              <Crown size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Choose Your Plan</h2>
            <p className="text-gray-400 mt-2">Unlock the full potential of your fashion journey</p>
          </div>
        </div>

        {/* Plans */}
        <div className="p-6 grid md:grid-cols-3 gap-4">
          {/* Free Plan */}
          <div 
            className={`rounded-xl p-5 border-2 cursor-pointer transition-all ${
              selectedPlan === 'free' 
                ? 'border-gray-500 bg-gray-500/10' 
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
            onClick={() => setSelectedPlan('free')}
          >
            <h3 className="text-lg font-semibold text-white">Free</h3>
            <div className="mt-2">
              <span className="text-2xl font-bold text-white">{symbol}0</span>
              <span className="text-gray-400 text-sm">/forever</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Perfect for trying out</p>
            <ul className="mt-4 space-y-2">
              {TIER_FEATURES.free.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                  <Check size={14} className="text-gray-500 mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Basic Plan */}
          <div 
            className={`rounded-xl p-5 border-2 cursor-pointer transition-all relative ${
              selectedPlan === 'basic-monthly' 
                ? 'border-purple-500 bg-purple-500/10' 
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
            onClick={() => setSelectedPlan('basic-monthly')}
          >
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              Basic <Zap size={16} className="text-purple-400" />
            </h3>
            <div className="mt-2">
              <span className="text-2xl font-bold text-white">{isIOS ? displayBasicPrice : `${symbol}${basicPrice}`}</span>
              <span className="text-gray-400 text-sm">/month</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">For casual fashion lovers</p>
            <ul className="mt-4 space-y-2">
              {TIER_FEATURES.basic.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <Check size={14} className="text-purple-400 mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>

          {/* Premium Plan - Best Value */}
          <div 
            className={`rounded-xl p-5 border-2 cursor-pointer transition-all relative ${
              selectedPlan === 'premium-monthly' 
                ? 'border-yellow-500 bg-gradient-to-br from-yellow-500/20 to-orange-500/20' 
                : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
            }`}
            onClick={() => setSelectedPlan('premium-monthly')}
          >
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                BEST VALUE
              </span>
            </div>
            
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              Premium <Crown size={16} className="text-yellow-400" />
            </h3>
            <div className="mt-2">
              <span className="text-2xl font-bold text-white">{isIOS ? displayPremiumPrice : `${symbol}${premiumPrice}`}</span>
              <span className="text-gray-400 text-sm">/month</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">For power users & creators</p>
            <ul className="mt-4 space-y-2">
              {TIER_FEATURES.premium.map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                  <Sparkles size={14} className="text-yellow-400 mt-0.5 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Action */}
        <div className="p-6 border-t border-gray-800">
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-200 text-sm">
              {error}
            </div>
          )}
          <Button 
            className="w-full py-6 text-lg bg-gradient-to-r from-stone-600 to-terracotta-600 hover:from-stone-700 hover:to-terracotta-700"
            onClick={handleSubscribe}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? (
              <>
                <Loader2 size={20} className="mr-2 animate-spin" />
                {isIOS ? 'Processing...' : 'Redirecting to checkout...'}
              </>
            ) : selectedPlan === 'premium-monthly' ? (
              <>
                <Crown size={20} className="mr-2" />
                Get Premium for {isIOS ? displayPremiumPrice : `${symbol}${premiumPrice}`}/month
              </>
            ) : selectedPlan === 'basic-monthly' ? (
              <>
                <Zap size={20} className="mr-2" />
                Get Basic for {isIOS ? displayBasicPrice : `${symbol}${basicPrice}`}/month
              </>
            ) : (
              'Continue with Free'
            )}
          </Button>
          <p className="text-center text-xs text-gray-500 mt-3">
            {isIOS ? (
              <>
                Payment will be charged to your Apple ID account at confirmation of purchase. 
                Subscription automatically renews unless it is cancelled at least 24 hours before the end of the current period. 
                Your account will be charged for renewal within 24 hours prior to the end of the current period. 
                You can manage and cancel your subscriptions by going to your account settings on the App Store after purchase.
              </>
            ) : (
              'Cancel anytime. No hidden fees. Secure payment via Stripe.'
            )}
          </p>
          <p className="text-center text-xs text-gray-500 mt-2">
            <a href="/terms" className="underline hover:text-gray-300">Terms of Service</a>
            {' · '}
            <a href="/privacy" className="underline hover:text-gray-300">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
